import { Injectable, signal, computed } from '@angular/core';
import { IIndividualInfoPayload } from '../../projects/clients/interfaces/onboarding/individual.interface';
import { ICompanyInfoPayload } from '../../projects/clients/interfaces/onboarding/company.interface';

type RegistrationType = 'INDIVIDUAL' | 'COMPANY' | null;

/**
 * OnboardingStateService
 *
 * Angular Signals-based state management for onboarding flow.
 *
 * Benefits:
 * - Security: No sensitive data persisted in localStorage (session-only)
 * - Type Safety: Strongly typed throughout
 * - Reactivity: Automatic UI updates when state changes
 * - Session-only: Data automatically cleared on browser close/refresh
 */
@Injectable({
  providedIn: 'root',
})
export class OnboardingStateService {
  // Private signals for state management
  private registrationType = signal<RegistrationType>(null);
  private individualData = signal<Partial<IIndividualInfoPayload> | null>(null);
  private companyData = signal<Partial<ICompanyInfoPayload> | null>(null);

  // Computed signals for derived state
  readonly hasOnboardingData = computed(
    () => this.individualData() !== null || this.companyData() !== null,
  );

  readonly isIndividual = computed(() => this.registrationType() === 'INDIVIDUAL');

  readonly isCompany = computed(() => this.registrationType() === 'COMPANY');

  /**
   * Store individual onboarding data
   */
  setIndividualData(data: Partial<IIndividualInfoPayload>): void {
    this.registrationType.set('INDIVIDUAL');
    this.individualData.set(data);
    this.companyData.set(null); // Clear company data if any
  }

  /**
   * Store company onboarding data
   */
  setCompanyData(data: Partial<ICompanyInfoPayload>): void {
    this.registrationType.set('COMPANY');
    this.companyData.set(data);
    this.individualData.set(null); // Clear individual data if any
  }

  /**
   * Get the combined payload for tax assessment submission
   */
  getTaxAssessmentPayload(): Partial<IIndividualInfoPayload | ICompanyInfoPayload> | null {
    if (this.isIndividual()) {
      return this.individualData();
    } else if (this.isCompany()) {
      return this.companyData();
    }
    return null;
  }

  /**
   * Get the current registration type
   */
  getRegistrationType(): RegistrationType {
    return this.registrationType();
  }

  /**
   * Clear all onboarding data
   */
  clearOnboardingData(): void {
    this.registrationType.set(null);
    this.individualData.set(null);
    this.companyData.set(null);
  }

  /**
   * Check if onboarding data exists (non-computed version for guards)
   */
  hasData(): boolean {
    return this.hasOnboardingData();
  }
}
